import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { fmtPercent } from '../../utils/format';
import { MiniSparkline } from './MiniSparkline';
import type { RevenueDelta } from './revenueFormat';

/**
 * Shared KPI card for the revenue dashboard (portfolio / site / machine). Beyond
 * the headline label + value it can optionally show:
 *   • a "vs prior <period>" delta line — green ▲ for a rise, red ▼ for a fall,
 *     muted for flat, and an em-dash when there is no comparison (no prior data
 *     or a zero prior base). Opt in with `showDelta`; the card computes nothing —
 *     the caller passes the already-computed `delta` + `deltaLabel`.
 *   • a mini sparkline (`sparkline` numeric series) under the value.
 *
 * Cards that have no prior signal (e.g. machines-online) simply omit `showDelta`.
 */
export function KpiCard({
  label, value, secondary, valueColor,
  showDelta, delta, deltaLabel, sparkline, sparklineColor, hint,
}: {
  label: string;
  value: string;
  secondary?: string;
  valueColor?: string;
  showDelta?: boolean;
  delta?: RevenueDelta | null;
  deltaLabel?: string;
  sparkline?: number[];
  sparklineColor?: string;
  /** Optional definition tooltip on the label — for KPIs whose meaning isn't
   *  self-evident (e.g. what "stale" measures). */
  hint?: string;
}) {
  const labelEl = <Typography variant="caption" color="text.secondary">{label}</Typography>;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {hint ? (
        <Tooltip title={hint} placement="top-start">
          <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
            {labelEl}
          </Box>
        </Tooltip>
      ) : labelEl}
      <Typography variant="h5" sx={{ fontWeight: 600, color: valueColor }}>{value}</Typography>
      {secondary && <Typography variant="body2" color="text.secondary">{secondary}</Typography>}
      {showDelta && <DeltaLine delta={delta ?? null} label={deltaLabel ?? 'vs prior period'} />}
      {sparkline && sparkline.length > 1 && (
        <Box sx={{ mt: 0.5 }}>
          <MiniSparkline data={sparkline} color={sparklineColor} ariaLabel={`${label} trend`} />
        </Box>
      )}
    </Paper>
  );
}

function DeltaLine({ delta, label }: { delta: RevenueDelta | null; label: string }) {
  // No comparison available (no prior data or a zero prior base) — em-dash.
  if (delta === null) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="kpi-delta">
        — {label}
      </Typography>
    );
  }
  const { pct, direction } = delta;
  const color = direction === 'up' ? 'success.main' : direction === 'down' ? 'error.main' : 'text.secondary';
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '';
  return (
    <Stack direction="row" spacing={0.5} alignItems="baseline" data-testid="kpi-delta">
      <Typography component="span" variant="body2" sx={{ color, fontWeight: 600 }}>
        {arrow}{arrow ? ' ' : ''}{fmtPercent(Math.abs(pct))}
      </Typography>
      <Typography component="span" variant="body2" color="text.secondary">{label}</Typography>
    </Stack>
  );
}
